import {
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { t } from 'ttag';
import {
  Field,
  useForm,
  useFormState,
} from 'react-final-form';

import ArrowRightIcon from '@svg/arrows-right.svg';
import UnorderedListIcon from '@svg/unordered-list.svg';
import OrderedListIcon from '@svg/ordered-list.svg';
import DoneIcon from '@svg/done.svg';
import BillIcon from '@svg/bill.svg';
import { PromoConditionType } from '@models';
import { required } from '@utils/validators';
import { useRegion } from '@hook/useRegion';
import { Modal, ModalProps } from '@ui/Modal';
import { Title } from '@ui/Title';
import { Alert } from '@ui/Alert';
import { Radio } from '@ui/Radio';
import { Input } from '@ui/Input';
import { Button } from '@ui/Button';
import { FormError } from '@ui/FormError';
import { ConfirmActionModal } from '@ui/ConfirmActionModal';

import s from './PromoActionFormModal.sss';

const HOW_IT_WORK_STEPS = [
  {
    icon: UnorderedListIcon,
    name: () => t`оберіть речі, які ви готові відправити з безкоштовною доставкою`,
  },
  {
    icon: OrderedListIcon,
    name: () => t`вкажіть умови, за яких ви готові запропонувати безкоштовну доставку`,
  },
  {
    icon: DoneIcon,
    name: () => t`для замовлень через Купити в накладній буде вказано, що
    доставку сплачує Відправник, якщо умови виконані покупцем`,
  },
  {
    icon: BillIcon,
    name: () => t`сплатіть доставку при відправці речей`,
  },
];

type Condition = {
  type: PromoConditionType
  value?: string
};

export type FormValues = {
  condition?: Condition
};

type Props = ModalProps & {
  title: string,
  description: string,
  important?: string | null,
  isActionActive?: boolean,
  onRequestRemovePromoAction: () => Promise<void>,
};

const getConditionRadioOptions = () => ([
  { label: t`Від суми`, value: PromoConditionType.FromPrice },
  { label: t`Від кількості речей`, value: PromoConditionType.FromItem },
  { label: t`Без умов`, value: PromoConditionType.Empty },
]);

type ConditionValueMeta = {
  placeholder: string
  unit: string | undefined
};

type ConditionValueMetaByConditionType = Record<PromoConditionType, ConditionValueMeta>;

export const PromoActionFormModal: React.FC<Props> = ({
  title,
  description,
  important,
  isActionActive,
  onRequestRemovePromoAction,
  className,
  isOpen,
  ...props
}) => {
  const region = useRegion();
  const form = useForm();
  const {
    values,
    submitError,
    submitting,
  } = useFormState<FormValues>();
  const [isCancelActionModalOpen, setIsCancelActionModalOpen] = useState(false);
  const [isRulesVisible, setIsRulesVisible] = useState(false);

  const conditionValueMeta = useMemo<ConditionValueMetaByConditionType>(
    () => ({
      [PromoConditionType.FromPrice]: {
        placeholder: t`Від 1000`,
        unit: region.currencyName,
      },
      [PromoConditionType.FromItem]: {
        placeholder: t`Від 2`,
        unit: t`речей`,
      },
      [PromoConditionType.Empty]: {
        placeholder: t`Без умов`,
        unit: undefined,
      },
    }),
    [region],
  );

  const isConditionValueInputDisabled = (
    !values.condition
    || values.condition.type === PromoConditionType.Empty
  );

  const handleResetConditionValue = () => {
    const fieldName = 'condition.value';
    form.change(fieldName, undefined);
    form.resetFieldState(fieldName);
  };

  return (
    <>
      <Modal
        withClose={true}
        {...props}
        className={classNames(s.root, className)}
        isOpen={isOpen && !isCancelActionModalOpen}
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await form.submit();
          }}
        >
          <Title type="h3" bold={false}>
            {title}
          </Title>
          <Alert className={s.alert}>
            {description}
          </Alert>

          <div className={s.conditions}>
            <Title type="h6" bold={false}>
              {t`Вкажіть умови`}
            </Title>

            <Field<Condition['type']>
              name="condition.type"
              validate={required}
              render={
              ({ input, meta }) => (
                <>
                  <div className={s.radioGroup}>
                    {
                      getConditionRadioOptions().map(
                        ({ label, value }) => (
                          <div
                            key={value}
                            className={s.radioLabel}
                            onClick={() => {
                              input.onChange(value);

                              if (value === PromoConditionType.Empty) {
                                handleResetConditionValue();
                              }
                            }}
                          >
                            <Radio
                              {...input}
                              value={value}
                              checked={input.value === value}
                            />
                            {label}
                          </div>
                        ),
                      )
                    }
                  </div>
                  {
                    ((meta.touched && meta.error) || meta.submitError)
                      && <FormError>{meta.error || meta.submitError}</FormError>
                  }
                </>
              )
            }
            />

            <div className={s.inputWithUnit}>
              <Field<Condition['value']>
              // Force rerender to update `validate` prop
                key={`condition.value.${isConditionValueInputDisabled}`}
                name="condition.value"
                type="number"
                validate={!isConditionValueInputDisabled ? required : undefined}
                render={
                ({ input, meta }) => (
                  <Input
                    className={s.conditionValueInput}
                    {...input}
                    placeholder={
                      values.condition?.type
                      && conditionValueMeta[values.condition.type]?.placeholder
                    }
                    disabled={isConditionValueInputDisabled}
                    error={(meta.touched && meta.error) || meta.submitError}
                  />
                )
              }
              />
              <div className={s.unit}>
                {
                values.condition?.type
                && conditionValueMeta[values.condition.type]?.unit
              }
              </div>
            </div>

            <div className={s.rules}>
              <Button
                className={classNames(s.rulesButton, {
                  [s.rulesButtonStateOpen]: isRulesVisible,
                })}
                theme="noTheme"
                onClick={() => setIsRulesVisible((prevState) => !prevState)}
              >
                {t`Як продавати речі з безкоштовною доставкою?`}

                <ArrowRightIcon
                  width="24"
                  height="24"
                />
              </Button>
              {isRulesVisible && (
              <ul className={s.rulesList}>
                {HOW_IT_WORK_STEPS.map(({ icon: Icon, name }, i) => (
                  <li
                    key={i}
                    className={s.rulesItem}
                  >
                    <Icon
                      className={s.rulesIcon}
                      width="24"
                      height="24"
                    />
                    <span className={s.rulesName}>
                      {name()}
                    </span>
                  </li>
                ))}
              </ul>
              )}
            </div>

            {!!important && (
            <div className={s.notice}>
              <span className={s.important}>{t`Важливо!`}</span>
              &nbsp;
              {important}
            </div>
            )}
          </div>

          <div className={s.buttons}>
            {isActionActive && (
            <Button
              theme="danger-text"
              onClick={() => setIsCancelActionModalOpen(true)}
            >
              {t`Прибрати товар з акції`}
            </Button>
            )}
            <Button
              type="submit"
              disabled={submitting}
            >
              {t`Зберегти`}
              {submitting && '...'}
            </Button>
          </div>
          {submitError && <FormError>{submitError}</FormError>}
        </form>
      </Modal>
      <ConfirmActionModal
        isOpen={isCancelActionModalOpen}
        onRequestClose={() => setIsCancelActionModalOpen(false)}
        onConfirm={async () => {
          await onRequestRemovePromoAction();
          setIsCancelActionModalOpen(false);
        }}
        confirmButtonText={t`Прибрати`}
        cancelButtonText={t`Відміна`}
      >
        <Title
          type="h3"
          bold={false}
        >
          {title}
        </Title>
        <p className={s.confirmationText}>
          {t`Прибрати товар з акції?`}
        </p>
      </ConfirmActionModal>
    </>
  );
};
